import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import { generateBillHtml } from '../../services/printService'; // Import the service

const BillPrintDialog = ({ open, onClose, bill, salesDetails }) => {
  const [printLanguage, setPrintLanguage] = useState('tamil');

  const handlePrintBill = () => {
    window.print();
  };

  if (!bill || !salesDetails) {
    return null;
  }

  // Generate the HTML using the centralized service and selected language
  const printHtml = generateBillHtml(bill, salesDetails, printLanguage);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="bill-print-dialog" // Class for print CSS
    >
      <DialogTitle sx={{ '@media print': { display: 'none' }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Print Bill
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#ffffff', minHeight: '500px', padding: 0 }}>
        {/* Print-specific CSS to hide non-dialog content and style the invoice */}
        <style>
          {`
            /* Base Styles for the Invoice Content */
            .invoice-container {
                max-width: 800px;
                margin: 0 auto;
                padding: 25px;
                background-color: #ffffff;
                font-family: 'Arial', sans-serif;
                color: #333;
            }
            .header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 15px;
                padding-bottom: 20px;
                border-bottom: 2px solid #f0f0f0;
            }
            .header .business-details { text-align: left; }
            .header h1 { margin: 0; font-size: 1.2em; color: #333; }
            .header h1 small { font-size: 0.8em; }
            .header p { margin: 5px 0; font-size: 0.9em; color: #555; }
            .contact-info { text-align: right; font-size: 0.9em; color: #444; }
            .contact-info .owner { font-weight: bold; }
            .details-section {
                display: flex;
                justify-content: space-between;
                margin-bottom: 15px;
                padding: 15px;
                background-color: #fafafa;
                border-radius: 5px;
            }
            .details-section div { font-size: 1em; }
            .details-section strong { color: #333; }
            .bill-table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 25px;
            }
            .bill-table th, .bill-table td {
                border: 1px solid #e0e0e0;
                padding: 12px;
                text-align: left;
            }
            .bill-table thead {
                background-color: #f5f5f5;
                color: #333;
                font-weight: bold;
            }
            .bill-table tbody tr:nth-child(even) { background-color: #fdfdfd; }
            .bill-table .text-right { text-align: right; }
            .totals-section {
                display: flex;
                justify-content: flex-end;
                margin-top: 20px;
            }
            .totals-table { width: 50%; max-width: 350px; }
            .totals-table td { padding: 10px 15px; font-size: 1.1em; }
            .totals-table .label { font-weight: bold; color: #555; }
            .totals-table .value { text-align: right; }
            .totals-table tr.balance td {
                font-weight: bold;
                font-size: 1.2em;
                color: #d32f2f;
                border-top: 2px solid #e0e0e0;
            }
            .footer {
                text-align: right;
                margin-top: 50px;
                padding-top: 20px;
                border-top: 1px solid #e0e0e0;
                color: #777;
            }
            .footer p { margin: 0; font-style: italic; }

            /* Print Media Query */
            @media print {
              body > *:not(.bill-print-dialog) {
                display: none !important;
              }
              .bill-print-dialog .MuiDialog-paper {
                margin: 0 !important;
                max-width: 100% !important;
                width: 100% !important;
                height: 100% !important;
                max-height: 100% !important;
                box-shadow: none !important;
                border-radius: 0 !important;
                background-color: #ffffff !important;
                position: absolute;
                top: 0;
                left: 0;
              }
              .bill-print-dialog .MuiDialogTitle-root,
              .bill-print-dialog .MuiDialogActions-root {
                display: none !important;
              }
              .bill-print-dialog .MuiDialogContent-root {
                background-color: #ffffff !important;
                padding: 0 !important;
                overflow: visible !important;
              }
              /* Ensure the invoice container fills the page properly */
              .invoice-container {
                  width: 100%;
                  max-width: 100%;
                  padding: 20px;
                  box-shadow: none;
                  border: none;
              }
            }
          `}
        </style>
        {/* Render the dynamically generated HTML */}
        <Box dangerouslySetInnerHTML={{ __html: printHtml }} />
      </DialogContent>
      <DialogActions sx={{ '@media print': { display: 'none' }, justifyContent: 'space-between', px: 3 }}>
        <ToggleButtonGroup
          value={printLanguage}
          exclusive
          onChange={(e, newLang) => { if (newLang) setPrintLanguage(newLang); }}
          aria-label="print language"
        >
          <ToggleButton value="tamil" aria-label="print in tamil">
            Tamil
          </ToggleButton>
          <ToggleButton value="english" aria-label="print in english">
            English
          </ToggleButton>
        </ToggleButtonGroup>
        <Box>
          <Button onClick={onClose}>Close</Button>
          <Button onClick={handlePrintBill} variant="contained" startIcon={<PrintIcon />}>
            Print
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BillPrintDialog;
